import React from 'react';
import TextField, {
    TextFieldProps,
} from 'components/interface/textField/TextField';
import { Popover, styled, Typography, useTheme } from '@mui/material';
import MenuList from 'components/interface/menu/MenuList';
import MenuItem from 'components/interface/menu/MenuItem';
import Popper from 'components/interface/Popper/Popper';
import {
    ResizeObserverCallback,
    useResizeObserverWithElement,
} from 'components/hooks/useResizeObserver';
import { IconButton } from 'components/IconButton';
import Symbol from 'components/Symbol';
import { LoadingSpinner } from 'components/interface/LoadingSpinner';

const Option = styled('div')(() => ({
    display: 'flex',
    flexDirection: 'column',
}));

const SelectorLoadingSpinner = styled(LoadingSpinner)({
    marginRight: '10px',
});

export interface SelectorProps<Type> extends Omit<
    TextFieldProps,
    'onChange' | 'autoComplete' | 'loading' | 'value'
> {
    options: Array<Type>;
    selected?: Type | null; // skip selected value if you want to always clear the text field
    inputValue?: string; // if you want to control the input value
    onInputChange?: (value: string) => void; // if you want to control the input value
    onChange: (value: Type | null) => void;
    renderOption?: (option: Type) => JSX.Element | null;
    getOptionLabel: (option: Type) => string;
    getOptionSubtitle?: (option: Type) => React.ReactNode; // subtitle for the option
    compareFunc: (comparison: Type, selected?: Type | null) => boolean;

    // if autocomplete is false: it will filter out the options unless they are selected
    // if autocomplete is true: it will filter out the options based on the input value
    filterFunc?: (
        option: Type,
        inputValue: string,
        inputValueLowerCase: string
    ) => boolean;
    getIndexGroup?: (index: number) => string | null;
    getColor?: (option: Type) => string | null;
    autocomplete?: boolean;
    autocompleteRenderMax?: number; // max number of items to render in autocomplete
    autocompleteKeepInputValue?: boolean; // keep the input value when selecting an item and openin the menu again
    firstChoiceLabel?: string;
    isMobile?: boolean;
    resize?: boolean; // resize with content
    placeholder?: string; // only visable for autocomplete when nothing is selected
    keepOpen?: boolean; // if the menu should be kept open on select
    async?: boolean; // if the options are fetched async. If true the selector wont filter the options. It will force the autocomplete prop to true.
    loading?: boolean;
    cacheBuster?: number;
    showSpinner?: boolean;

    onClear?: () => void;
    hideArrowOnSelect?: boolean; // if the arrow should be hidden when an item is selected
    autoSelectFirstOption?: boolean; // If the first option should be selected from the beginning
    allowFreeText?: boolean; // If the user should be able to write free text
    hidePlaceholder?: boolean; // If the placeholder should be hidden when the input is empty
    noResultsMessage?: string;
}

export default function Selector<Type>({
    options,
    selected = null,
    inputValue: controlledInputValue,
    onChange,
    onInputChange,
    renderOption,
    getOptionLabel,
    getOptionSubtitle,
    compareFunc,
    getIndexGroup,
    getColor,
    filterFunc,
    firstChoiceLabel,
    isMobile,
    resize,
    fullWidth,
    disabled,
    placeholder,
    autocomplete = false,
    autocompleteRenderMax,
    autocompleteKeepInputValue,
    keepOpen = false,
    async = false,
    loading,
    cacheBuster = 0,
    required,
    showSpinner = false,
    hideArrowOnSelect = true,
    onClear,
    helperText,
    onBlur,
    autoSelectFirstOption,
    allowFreeText,
    sx,
    variantSx,
    inputSx,
    variant,
    hidePlaceholder,
    error,
    noResultsMessage = 'Inga resultat',
    ...rest
}: SelectorProps<Type>) {
    const [_inputValue, _setInputValue] = React.useState('');

    const inputValue = onInputChange ? controlledInputValue : _inputValue;
    const setInputValue = onInputChange ? onInputChange : _setInputValue;

    const [hoverIndex, setHoverIndex] = React.useState(-1);
    const isSelected = selected !== null;

    // sets the valus based on the props
    autocomplete = autocomplete || async;
    autocompleteKeepInputValue = autocomplete
        ? autocompleteKeepInputValue
        : false;

    const placeHolderValue = React.useMemo(() => {
        if (placeholder !== undefined) {
            return placeholder;
        }
        if (autocomplete) {
            return 'Sök...';
        }
        return 'Välj';
    }, [placeholder, autocomplete]);

    const selectedLabel = (() => {
        if (isSelected) {
            // this is so it can display the text even when the selected value is not in the options
            if (autocomplete && selected) {
                return getOptionLabel(selected);
            } else if (allowFreeText) {
                return inputValue || '';
            }

            for (let i = 0; i < options.length; i++) {
                if (compareFunc(options[i], selected)) {
                    return getOptionLabel(options[i]);
                }
            }
        }

        if (allowFreeText) {
            return inputValue || '';
        }

        if (autocomplete) {
            return '';
        }

        return firstChoiceLabel || placeholder || '';
    })();

    const theme = useTheme();
    const [open, setOpen] = React.useState(false);
    const [menuAnchorEl, setMenuAnchorEl] =
        React.useState<HTMLInputElement | null>(null);
    const fieldRef = React.useRef<HTMLDivElement>(null);
    const selectedItem = React.useRef<HTMLButtonElement>(null);
    const hoveredItem = React.useRef<HTMLButtonElement>(null);

    const handleMenuAnchorRef = React.useCallback(
        (node: HTMLInputElement | null) => setMenuAnchorEl(node),
        []
    );

    const minWidth = menuAnchorEl?.clientWidth
        ? menuAnchorEl.clientWidth
        : null;

    const maxWidth = Math.max(minWidth || 0, 500);

    const inputPadding = useInputPadding(resize ? menuAnchorEl : null);

    const labelWidth = React.useMemo(() => {
        if (!resize) {
            return 0;
        }

        const measureTextWidth = (text: string, font: string): number => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            if (!context) return 0;

            context.font = font || getComputedStyle(document.body).font;
            return context.measureText(text).width;
        };

        // This is a fallback if the menuAnchor is not set, initial rendering.
        let font = '16px Inter, sans-serif';
        if (menuAnchorEl) {
            font = window.getComputedStyle(menuAnchorEl).font;
        }

        const breathingRoom = 12;
        const baseWidth = measureTextWidth(selectedLabel, font);

        const finalWidth = baseWidth + inputPadding + breathingRoom;

        return Math.round(finalWidth);
    }, [inputPadding, menuAnchorEl, selectedLabel, resize]);

    const { optionsToRender, hiddenOptionsCount } = React.useMemo(() => {
        if (async) {
            return { optionsToRender: options, hiddenOptionsCount: 0 };
        }

        const filterF =
            filterFunc ||
            ((option: Type, _query: string, queryLowerCase: string) =>
                getOptionLabel(option)
                    .toLowerCase()
                    .startsWith(queryLowerCase));

        const inputValueLowerCase = inputValue ? inputValue.toLowerCase() : '';
        let hiddenOptionsCount = 0;
        const arr: Type[] = [];
        for (let i = 0; i < options.length; i++) {
            const option = options[i];
            if (
                autocomplete &&
                inputValue &&
                !filterF(option, inputValue, inputValueLowerCase)
            ) {
                continue;
            } else if (
                !autocomplete &&
                filterFunc &&
                !filterF(option, '', '') &&
                !compareFunc(option, selected)
            ) {
                continue;
            }

            if (autocompleteRenderMax && arr.length >= autocompleteRenderMax) {
                hiddenOptionsCount++;
                continue;
            }

            arr.push(option);
        }
        if (hoverIndex >= arr.length) {
            setHoverIndex(arr.length - 1);
        }
        return { optionsToRender: arr, hiddenOptionsCount };
    }, [open, options.length, inputValue, async, cacheBuster]);

    React.useEffect(() => {
        if (autoSelectFirstOption) {
            if (options.length > 0 && !isSelected) {
                onChange(options[0]);
            }
        }
    }, [options.length]);

    React.useEffect(() => {
        // Some small timout is needed, 1 seems to work but better be safe.
        setTimeout(() => {
            if (selectedItem.current && open) {
                selectedItem.current.scrollIntoView({
                    behavior: 'auto',
                    block: 'center',
                    inline: 'center',
                });

                for (let i = 0; i < optionsToRender.length; i++) {
                    if (compareFunc(optionsToRender[i], selected)) {
                        setHoverIndex(i);
                        break;
                    }
                }
            }
        }, 10);
    }, [open]);

    React.useEffect(() => {
        // Some small timout is needed, 1 seems to work but better be safe.
        setTimeout(() => {
            if (hoveredItem.current && open) {
                hoveredItem.current.scrollIntoView({
                    behavior: 'auto',
                    block: 'nearest',
                    inline: 'nearest',
                });
            }
        }, 10);
    }, [hoveredItem.current, hoverIndex]);

    // On close set input to the selected label
    React.useEffect(() => {
        if (!open) {
            setHoverIndex(-1);

            if (allowFreeText) return;

            if ((async || autocompleteKeepInputValue) && selected) {
                setInputValue(getOptionLabel(selected));
            } else {
                // if async and selected, keep the input value
                setInputValue('');
            }
        }
    }, [open]);

    const handleOnChange = (value: Type | null) => {
        if (!keepOpen) {
            setOpen(false);
        }
        // if async and value is set, set the input value to the selected value
        // this is so if you click to search it will prefill the input with the selected value
        if ((async || autocompleteKeepInputValue) && value) {
            setInputValue(getOptionLabel(value));
        } else {
            setInputValue('');
        }
        onChange(value);
    };

    const handleOnClear = () => {
        if (async || autocompleteKeepInputValue) {
            handleOnChange(null);
        }
        if (onClear) {
            onClear();
        }
    };

    const handleOnClick = (value?: Type | null) => {
        if (value !== undefined) {
            handleOnChange(value);
        }
    };

    const incrementHoveredIndex = () =>
        setHoverIndex(prev => {
            if (prev < optionsToRender.length - 1) {
                return prev + 1;
            }
            return 0;
        });

    const decrementHoveredIndex = () =>
        setHoverIndex(prev => {
            if (prev > 0) {
                return prev - 1;
            }
            if (prev === -1) {
                return 0;
            }
            return optionsToRender.length - 1;
        });

    const selectHoveredItem = () => {
        const value = optionsToRender[hoverIndex];
        if (value) {
            // if async and value is set, set the input value to the selected value
            // this is so if you click to search it will prefill the input with the selected value
            if ((async || autocompleteKeepInputValue) && value) {
                setInputValue(getOptionLabel(value));
            } else {
                setInputValue('');
            }
            onChange(value);
        }
        if (!keepOpen) {
            setOpen(false);
        }
    };

    // Only used when not using autocompelte
    const hoverOptionStartingWith = (s: string) => {
        if (autocomplete || async) {
            return;
        }
        let l = -1; // the lowest available
        let h = -1; // the first available with higher index than current
        for (let i = 0; i < options.length; i++) {
            const label = getOptionLabel(options[i]);
            if (label[0].toLowerCase() === s) {
                if (i === hoverIndex) {
                    continue;
                }
                if (l === -1) {
                    l = i;
                }
                if (h === -1 && i > hoverIndex) {
                    h = i;
                }
                setHoverIndex(i);
            }
        }
        if (h > -1 && s === inputValue) {
            setHoverIndex(h);
        } else if (l === -1) {
            // do nothing
        } else {
            setHoverIndex(l);
        }
        setInputValue(s);
    };

    const handleOnKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        e.stopPropagation();
        if (open) {
            if (variant === 'sheet') {
                // Supprt closing the menu with arrow keys
                if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
                    setOpen(false);
                    return;
                }
            }

            if (e.key === 'Escape') {
                e.preventDefault();
                setOpen(false);
            } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                decrementHoveredIndex();
            } else if (e.key === 'ArrowDown') {
                e.preventDefault();
                incrementHoveredIndex();
            } else if (e.key === 'Enter') {
                e.preventDefault();
                selectHoveredItem();
            } else if (e.key === 'Tab') {
                setOpen(false);
            } else if (
                !autocomplete &&
                ((e.keyCode >= 65 && e.keyCode <= 90) ||
                    (parseInt(e.key) > 0 && parseInt(e.key) < 10))
            ) {
                hoverOptionStartingWith(e.key);
            }
        } else {
            if (
                e.key !== 'Tab' &&
                e.key !== 'Control' &&
                e.key !== 'Shift' &&
                e.key !== 'Alt' &&
                e.key !== 'ArrowLeft' &&
                e.key !== 'ArrowRight' &&
                e.key !== 'Meta'
            ) {
                if (variant === 'sheet') {
                    // Allow navigating up and down with arrow keys
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                        return;
                    }
                    if (e.key === 'Delete') {
                        handleOnClear();
                        e.preventDefault();
                        return;
                    }
                }
                setOpen(true);
                if (!autocomplete) {
                    e.preventDefault();
                }
            }
        }
    };

    const renderMenu = () => (
        <MenuList>
            {firstChoiceLabel && (
                <MenuItem
                    onClick={() => handleOnClick(null)}
                    disabled={disabled}
                    selected={!isSelected}>
                    {firstChoiceLabel}
                </MenuItem>
            )}
            {optionsToRender.length === 0 ? (
                <MenuItem onClick={() => null} disabled={true}>
                    {noResultsMessage}
                </MenuItem>
            ) : (
                optionsToRender.map((option, i) => {
                    const isSelected = compareFunc(option, selected);
                    const isHovered = i === hoverIndex;
                    const groupName =
                        getIndexGroup && (!autocomplete || async)
                            ? getIndexGroup(i)
                            : null;
                    const color = getColor && getColor(option);

                    return (
                        <React.Fragment key={i}>
                            {groupName && (
                                <MenuItem
                                    disabled={true}
                                    underline
                                    onClick={() => null}>
                                    {groupName}
                                </MenuItem>
                            )}
                            <MenuItem
                                ref={
                                    isHovered
                                        ? hoveredItem
                                        : isSelected
                                          ? selectedItem
                                          : undefined
                                }
                                onClick={() => handleOnClick(option)}
                                color={color}
                                disabled={disabled}
                                selected={isSelected}
                                hover={isHovered}>
                                {renderOption && option ? (
                                    renderOption(option)
                                ) : (
                                    <Option>
                                        <Typography
                                            variant='body2'
                                            color='inherit'
                                            fontWeight={500}>
                                            {getOptionLabel(option)}
                                        </Typography>
                                        {getOptionSubtitle && (
                                            <>
                                                {typeof getOptionSubtitle(
                                                    option
                                                ) === 'string' ? (
                                                    <Typography
                                                        variant='caption'
                                                        color='textSecondary'>
                                                        {getOptionSubtitle(
                                                            option
                                                        )}
                                                    </Typography>
                                                ) : (
                                                    getOptionSubtitle(option)
                                                )}
                                            </>
                                        )}
                                    </Option>
                                )}
                            </MenuItem>
                        </React.Fragment>
                    );
                })
            )}
            {hiddenOptionsCount > 0 && (
                <MenuItem onClick={() => null} disabled={true} underline>
                    {hiddenOptionsCount} dolda resultat
                </MenuItem>
            )}
        </MenuList>
    );

    const endIcon = React.useMemo(() => {
        if (disabled) {
            return null;
        }

        if (open) {
            return (
                <>
                    <SelectorLoadingSpinner
                        loading={Boolean(loading && showSpinner)}
                        size={20}
                    />
                    <Symbol symbol='keyboard_arrow_up' size='sm' />
                </>
            );
        } else {
            let showClearButton = false;
            if (async) {
                showClearButton = !!selected;
            } else if (onClear) {
                showClearButton = !!selected || autocomplete;
            }
            return (
                <>
                    {showClearButton && (
                        <IconButton
                            icon='close'
                            size='xs'
                            variant='text'
                            onClick={e => {
                                e.stopPropagation();
                                handleOnClear();
                            }}
                        />
                    )}
                    {showClearButton && hideArrowOnSelect ? null : (
                        <Symbol symbol='keyboard_arrow_down' size='sm' />
                    )}
                </>
            );
        }
    }, [open, loading, showSpinner, disabled, onClear, async, selected]);

    const { requiredError, requiredText } = React.useMemo(() => {
        if (!allowFreeText) {
            return {
                requiredError: required && !selected && !loading,
                requiredText: 'Välj ett alternativ',
            };
        } else {
            return {
                requiredError: inputValue === '' && required,
                requiredText: '',
            };
        }
    }, [inputValue, required, selected, loading, allowFreeText]);

    return (
        <>
            <div ref={fieldRef} style={{ display: 'contents' }}>
                <TextField
                    ref={handleMenuAnchorRef}
                    variant={variant}
                    value={autocomplete && open ? inputValue : selectedLabel}
                    placeholder={placeHolderValue}
                    {...rest}
                    onBlur={e => {
                        if (onBlur) {
                            onBlur(e);
                        }
                    }}
                    autoComplete='off'
                    onChange={setInputValue}
                    onBaseClick={
                        disabled
                            ? undefined
                            : () => {
                                  if (!open) {
                                      // should not clear the input on open for async
                                      if (!(async || autocompleteKeepInputValue)) {
                                          setInputValue('');
                                      }
                                      setOpen(true);
                                  }
                              }
                    }
                    onKeyDown={handleOnKeyDown}
                    disabled={disabled}
                    endAdornment={endIcon}
                    error={requiredError || error}
                    helperText={requiredError ? requiredText : helperText}
                    required={required}
                    loading={loading && !showSpinner && !autocomplete}
                    readOnly={!autocomplete}
                    fullWidth={fullWidth}
                    variantSx={{
                        ...variantSx,
                        cursor: autocomplete ? 'text' : 'pointer',
                    }}
                    inputSx={{
                        ...inputSx,
                        cursor: autocomplete ? 'text' : 'pointer',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                    sx={{
                        ...sx,
                        width:
                            resize && !fullWidth
                                ? `${labelWidth}px !important`
                                : undefined,
                    }}
                />
            </div>
            {autocomplete ? (
                <Popper
                    open={open}
                    anchorEl={menuAnchorEl}
                    PaperProps={{
                        style: {
                            minWidth: minWidth || 'unset',
                            maxWidth: maxWidth,
                            maxHeight: async
                                ? 'min(40dvh, 700px)'
                                : 'min(30dvh, 500px)',
                            overflow: 'auto',
                        },
                        elevation: 8,
                    }}
                    TransitionProps={{
                        exit: !autocomplete,
                    }}
                    transition
                    handleClose={e => {
                        if (
                            e.target &&
                            !fieldRef.current?.contains(e.target as Node)
                        ) {
                            setOpen(false);
                        }
                    }}
                    placement='bottom'>
                    {renderMenu()}
                </Popper>
            ) : (
                <Popover
                    open={open}
                    anchorEl={menuAnchorEl}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                    }}
                    PaperProps={{
                        style: {
                            minWidth: minWidth || 'unset',
                            maxWidth: maxWidth,
                            maxHeight: 'calc(100% - 32px)',
                            overflow: 'auto',
                        },
                    }}
                    transitionDuration={{
                        enter: theme.transitions.duration.standard,
                        exit: theme.transitions.duration.leavingScreen,
                    }}
                    onClose={() => setOpen(false)}
                    onKeyDown={handleOnKeyDown}>
                    {renderMenu()}
                </Popover>
            )}
        </>
    );
}

const useInputPadding = (target: Element | null) => {
    const [size, setSize] = React.useState<number>(0);

    const callback: ResizeObserverCallback = React.useCallback(entry => {
        const computedStyle = window.getComputedStyle(entry.target);
        const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
        const paddingRight = parseFloat(computedStyle.paddingRight) || 0;

        setSize(paddingLeft + paddingRight);
    }, []);

    useResizeObserverWithElement(target, callback, () => setSize(0));

    return size;
};
